// Pure helpers for the store ("muro de cartazes"): poster state, access
// windows, price formatting. No React here so they stay unit-testable.

export type PosterState = 'new' | 'owned' | 'expired' | 'pending';

/** Which face a poster shows, from the access flags the server returns. */
export function posterStateOf(access: {
  owned: boolean;
  expired: boolean;
  pending: boolean;
}): PosterState {
  if (access.pending) return 'pending';
  if (access.owned && access.expired) return 'expired';
  if (access.owned) return 'owned';
  return 'new';
}

/** Access window in whole months (null = no time limit). 12 months ≈ 365d. */
export function monthsFromDays(days: number | null | undefined): number | null {
  if (days == null) return null;
  return Math.max(1, Math.round(days / 30));
}

/** The day a window opened today would close (null = never). */
export function accessEndDate(
  days: number | null | undefined,
  today: Date = new Date(),
): Date | null {
  if (days == null) return null;
  const end = new Date(today);
  end.setDate(end.getDate() + days);
  return end;
}

/** "R$ 149" for whole amounts, "R$ 49,90" otherwise; '' for free (caller shows "Grátis"). */
export function formatPrice(
  priceCents: number,
  currency: string,
  language: string,
): string {
  const amount = priceCents / 100;
  const whole = priceCents % 100 === 0;
  try {
    return new Intl.NumberFormat(language, {
      style: 'currency',
      currency,
      minimumFractionDigits: whole ? 0 : 2,
      maximumFractionDigits: whole ? 0 : 2,
    }).format(amount);
  } catch {
    // Unknown/blank currency code: fall back to a plain number + code.
    return `${whole ? amount.toFixed(0) : amount.toFixed(2)} ${currency}`.trim();
  }
}

/** Four poster inks, rotated by position on the wall. */
export type PosterInk = 'red' | 'pink' | 'black' | 'cream';
const INKS: PosterInk[] = ['red', 'pink', 'black', 'cream'];
export function posterInkOf(index: number): PosterInk {
  return INKS[((index % INKS.length) + INKS.length) % INKS.length]!;
}

/** Stable ink per product, so a poster keeps its colour on the wall and on its own page. */
export function posterInkForProduct(productId: string): PosterInk {
  let sum = 0;
  for (let i = 0; i < productId.length; i++) sum += productId.charCodeAt(i);
  return posterInkOf(sum);
}

/**
 * Which carousel slide is in view, from the viewport's scroll position: every
 * slide is exactly one viewport wide, so it is the nearest whole page. Clamped
 * to the slide count; a zero-width viewport (not laid out yet) reads slide 0.
 */
export function slideIndexFromScroll(scrollLeft: number, viewportWidth: number, total: number): number {
  if (total <= 0) return 0;
  if (viewportWidth <= 0) return 0;
  const i = Math.round(scrollLeft / viewportWidth);
  return Math.max(0, Math.min(total - 1, i));
}
